import { useState } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';

interface Payment {
  id: string;
  orderId: string;
  studentName: string;
  courseName: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  paymentMethod: string;
  transactionId: string;
  createdAt: string;
  refundAmount?: number;
}

const AdminPayments = () => {
  const [payments] = useState<Payment[]>([
    {
      id: '1',
      orderId: 'ORD-2024-001',
      studentName: '김민수',
      courseName: '디지털 마케팅 전문가 과정',
      amount: 5000,
      status: 'completed',
      paymentMethod: '신용카드',
      transactionId: 'TXN-20240101-001',
      createdAt: '2024-01-15T10:30:00Z'
    },
    {
      id: '2',
      orderId: 'ORD-2024-002',
      studentName: '이영희',
      courseName: 'React 개발자 과정',
      amount: 8000,
      status: 'pending',
      paymentMethod: '계좌이체',
      transactionId: 'TXN-20240102-001',
      createdAt: '2024-01-16T14:20:00Z'
    },
    {
      id: '3',
      orderId: 'ORD-2024-003',
      studentName: '박철수',
      courseName: '데이터 분석 기초',
      amount: 3000,
      status: 'failed',
      paymentMethod: '신용카드',
      transactionId: 'TXN-20240103-001',
      createdAt: '2024-01-17T09:15:00Z'
    },
    {
      id: '4',
      orderId: 'ORD-2024-004',
      studentName: '정수진',
      courseName: 'UI/UX 디자인 과정',
      amount: 7000,
      status: 'refunded',
      paymentMethod: '신용카드',
      transactionId: 'TXN-20240104-001',
      createdAt: '2024-01-18T16:45:00Z',
      refundAmount: 7000
    },
    {
      id: '5',
      orderId: 'ORD-2024-005',
      studentName: '최영수',
      courseName: '프로젝트 관리 전문가',
      amount: 10000,
      status: 'completed',
      paymentMethod: '신용카드',
      transactionId: 'TXN-20240105-001',
      createdAt: '2024-01-19T11:20:00Z'
    },
    {
      id: '6',
      orderId: 'ORD-2024-006',
      studentName: '한지민',
      courseName: '클라우드 컴퓨팅 기초',
      amount: 4000,
      status: 'completed',
      paymentMethod: '계좌이체',
      transactionId: 'TXN-20240106-001',
      createdAt: '2024-01-20T15:45:00Z'
    },
    {
      id: '7',
      orderId: 'ORD-2024-007',
      studentName: '송민호',
      courseName: 'AI 활용 비즈니스 전략',
      amount: 6000,
      status: 'completed',
      paymentMethod: '신용카드',
      transactionId: 'TXN-20240107-001',
      createdAt: '2024-01-21T09:30:00Z'
    },
    {
      id: '8',
      orderId: 'ORD-2024-008',
      studentName: '김하늘',
      courseName: '웹 개발 기초',
      amount: 2000,
      status: 'pending',
      paymentMethod: '계좌이체',
      transactionId: 'TXN-20240108-001',
      createdAt: '2024-01-22T13:15:00Z'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return '완료';
      case 'pending': return '대기중';
      case 'failed': return '실패';
      case 'refunded': return '환불';
      default: return status;
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.orderId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalRevenue = payments
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount, 0);

  const pendingAmount = payments
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + p.amount, 0);

  const refundedAmount = payments
    .filter(p => p.status === 'refunded')
    .reduce((sum, p) => sum + (p.refundAmount || 0), 0);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPayments(filteredPayments.map(p => p.id));
    } else {
      setSelectedPayments([]);
    }
  };

  const handleSelectPayment = (paymentId: string, checked: boolean) => {
    if (checked) {
      setSelectedPayments([...selectedPayments, paymentId]);
    } else {
      setSelectedPayments(selectedPayments.filter(id => id !== paymentId));
    }
  };

  const handleRefund = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowRefundModal(true);
  };

  const handleViewDetail = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowDetailModal(true);
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">결제 관리</h1>
          <p className="text-gray-600">결제 내역을 조회하고 관리할 수 있습니다.</p>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <i className="ri-money-dollar-circle-line text-xl text-blue-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 매출</p>
                <p className="text-2xl font-bold text-gray-900">₩{totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <i className="ri-time-line text-xl text-yellow-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">대기중 결제</p>
                <p className="text-2xl font-bold text-gray-900">₩{pendingAmount.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <i className="ri-refund-line text-xl text-red-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">환불 금액</p>
                <p className="text-2xl font-bold text-gray-900">₩{refundedAmount.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <i className="ri-file-list-3-line text-xl text-green-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 거래</p>
                <p className="text-2xl font-bold text-gray-900">{payments.length}건</p>
              </div>
            </div>
          </div>
        </div>

        {/* 검색 및 필터 */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="학습자명, 과정명, 주문번호로 검색..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <select
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">전체 상태</option>
                  <option value="completed">완료</option>
                  <option value="pending">대기중</option>
                  <option value="failed">실패</option>
                  <option value="refunded">환불</option>
                </select>

                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
                  <i className="ri-download-line mr-2"></i>
                  엑셀 다운로드
                </button>
              </div>
            </div>
          </div>

          {/* 일괄 작업 */}
          {selectedPayments.length > 0 && (
            <div className="p-4 bg-blue-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-700">
                  {selectedPayments.length}개 항목이 선택되었습니다.
                </span>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1 text-sm bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-50 transition-colors whitespace-nowrap">
                    일괄 환불 처리
                  </button>
                  <button className="px-3 py-1 text-sm bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-50 transition-colors whitespace-nowrap">
                    엑셀 내보내기
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 결제 목록 테이블 */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={selectedPayments.length === filteredPayments.length && filteredPayments.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    주문정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    학습자
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    과정명
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    결제금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    결제방법
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    결제일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={selectedPayments.includes(payment.id)}
                        onChange={(e) => handleSelectPayment(payment.id, e.target.checked)}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{payment.orderId}</div>
                        <div className="text-sm text-gray-500">{payment.transactionId}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{payment.studentName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{payment.courseName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        ₩{payment.amount.toLocaleString()}
                      </div>
                      {payment.refundAmount && (
                        <div className="text-sm text-red-600">
                          환불: ₩{payment.refundAmount.toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{payment.paymentMethod}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                        {getStatusText(payment.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {new Date(payment.createdAt).toLocaleDateString('ko-KR')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleViewDetail(payment)}
                          className="text-blue-600 hover:text-blue-900 text-sm whitespace-nowrap"
                        >
                          상세보기
                        </button>
                        {payment.status === 'completed' && (
                          <button 
                            onClick={() => handleRefund(payment)}
                            className="text-red-600 hover:text-red-900 text-sm whitespace-nowrap"
                          >
                            환불
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredPayments.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-file-list-3-line text-4xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">결제 내역이 없습니다</h3>
              <p className="text-gray-500">검색 조건을 변경해보세요.</p>
            </div>
          )}
        </div>

        {/* 결제 상세 모달 */}
        {showDetailModal && selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">결제 상세 정보</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* 기본 정보 */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">기본 정보</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">주문번호</label>
                      <p className="text-sm text-gray-900">{selectedPayment.orderId}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">거래번호</label>
                      <p className="text-sm text-gray-900">{selectedPayment.transactionId}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">결제 상태</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedPayment.status)}`}>
                        {getStatusText(selectedPayment.status)}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">결제일시</label>
                      <p className="text-sm text-gray-900">
                        {new Date(selectedPayment.createdAt).toLocaleString('ko-KR')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 학습자 정보 */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">학습자 정보</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">이름</label>
                        <p className="text-sm text-gray-900">{selectedPayment.studentName}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">이메일</label>
                        <p className="text-sm text-gray-900">{selectedPayment.studentName.toLowerCase().replace(/\s+/g, '')}@example.com</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">연락처</label>
                        <p className="text-sm text-gray-900">010-1234-5678</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">학습자 ID</label>
                        <p className="text-sm text-gray-900">STU-{selectedPayment.id.padStart(6, '0')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 과정 정보 */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">과정 정보</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">과정명</label>
                        <p className="text-sm text-gray-900 font-medium">{selectedPayment.courseName}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">과정 기간</label>
                          <p className="text-sm text-gray-900">8주</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">수강 시작일</label>
                          <p className="text-sm text-gray-900">
                            {new Date(selectedPayment.createdAt).toLocaleDateString('ko-KR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 결제 정보 */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">결제 정보</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">결제 방법</label>
                        <p className="text-sm text-gray-900">{selectedPayment.paymentMethod}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">결제 금액</label>
                        <p className="text-lg font-bold text-gray-900">₩{selectedPayment.amount.toLocaleString()}</p>
                      </div>
                      {selectedPayment.paymentMethod === '신용카드' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">카드 정보</label>
                            <p className="text-sm text-gray-900">**** **** **** 1234</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">할부</label>
                            <p className="text-sm text-gray-900">일시불</p>
                          </div>
                        </>
                      )}
                      {selectedPayment.refundAmount && (
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-500 mb-1">환불 정보</label>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-sm text-red-800 font-medium">
                              환불 금액: ₩{selectedPayment.refundAmount.toLocaleString()}
                            </p>
                            <p className="text-xs text-red-600 mt-1">
                              환불 처리일: {new Date().toLocaleDateString('ko-KR')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 추가 정보 */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">추가 정보</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">결제 IP</label>
                        <p className="text-sm text-gray-900">192.168.1.100</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">사용자 에이전트</label>
                        <p className="text-sm text-gray-900 break-all">
                          Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">메모</label>
                        <p className="text-sm text-gray-900">정상 결제 완료</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  닫기
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
                  <i className="ri-printer-line mr-2"></i>
                  영수증 출력
                </button>
                {selectedPayment.status === 'completed' && (
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleRefund(selectedPayment);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refund-line mr-2"></i>
                    환불 처리
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 환불 모달 */}
        {showRefundModal && selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium text-gray-900 mb-4">환불 처리</h3>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">주문번호: {selectedPayment.orderId}</p>
                <p className="text-sm text-gray-600 mb-2">학습자: {selectedPayment.studentName}</p>
                <p className="text-sm text-gray-600 mb-2">결제금액: ₩{selectedPayment.amount.toLocaleString()}</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  환불 금액
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  defaultValue={selectedPayment.amount}
                  max={selectedPayment.amount}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  환불 사유
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="환불 사유를 입력하세요..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowRefundModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  취소
                </button>
                <button
                  onClick={() => {
                    setShowRefundModal(false);
                    setSelectedPayment(null);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
                >
                  환불 처리
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPayments;
